import { useEffect, useId, useMemo, useSyncExternalStore } from 'react';

interface UseDialogStackOptions {
  baseZIndex?: number;
}

const listeners = new Set<() => void>();
let dialogOrder: string[] = [];
const EMPTY_DIALOG_ORDER: string[] = [];

const emitChange = () => {
  listeners.forEach(listener => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => dialogOrder;

const upsertDialog = (dialogId: string) => {
  const nextOrder = [...dialogOrder.filter(id => id !== dialogId), dialogId];

  if (nextOrder.length !== dialogOrder.length || nextOrder.some((id, index) => id !== dialogOrder[index])) {
    dialogOrder = nextOrder;
    emitChange();
  }
};

const removeDialog = (dialogId: string) => {
  const nextOrder = dialogOrder.filter(id => id !== dialogId);

  if (nextOrder.length !== dialogOrder.length) {
    dialogOrder = nextOrder;
    emitChange();
  }
};

export const useDialogStack = (open: boolean, { baseZIndex = 1400 }: UseDialogStackOptions = {}) => {
  const dialogId = useId();
  const order = useSyncExternalStore(subscribe, getSnapshot, () => EMPTY_DIALOG_ORDER);

  useEffect(() => {
    if (!open) {
      removeDialog(dialogId);
      return;
    }

    upsertDialog(dialogId);

    return () => {
      removeDialog(dialogId);
    };
  }, [dialogId, open]);

  return useMemo(() => {
    const currentIndex = order.indexOf(dialogId);
    const layerIndex = open ? (currentIndex >= 0 ? currentIndex : order.length) : 0;
    const overlayZIndex = baseZIndex + layerIndex * 2;

    return {
      contentZIndex: overlayZIndex + 1,
      overlayZIndex
    };
  }, [baseZIndex, dialogId, open, order]);
};
